// Shared helpers for displaying rows from /api/revisions

// Noisy or sensitive revisions that are never shown
export const EXCLUDED_KEYS = ['updated_at', 'password', 'is_assessor'];

export const PREVIEW_LENGTH = 80;
export const DAY_MS = 24 * 60 * 60 * 1000;

// Hue per type so the same type always gets the same badge colour
const TYPE_HUES = [210, 150, 30, 280, 350, 180, 60, 250, 0, 120];

export function isEmptyValue(value) {
  return value === null || value === undefined || value === '';
}

// created / deleted revisions store the whole row as a JSON object
export function parseSnapshot(value) {
  if (typeof value !== 'string' || value[0] !== '{') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function preview(value) {
  const text = String(value);
  return text.length > PREVIEW_LENGTH ? text.substring(0, PREVIEW_LENGTH) + '…' : text;
}

// Precomputes everything the views and filters need once, sorted newest first
export function normaliseRevisions(rows, recordLabels = {}) {
  return rows
    .filter(r => !EXCLUDED_KEYS.includes(r.key))
    .map(r => {
      const action = r.key === 'created' || r.key === 'deleted' ? r.key : 'updated';
      const snapshot = action === 'created' ? parseSnapshot(r.new_value)
        : action === 'deleted' ? parseSnapshot(r.old_value) : null;
      const recordKey = `${r.revisionable_type}#${r.revisionable_id}`;
      const label = recordLabels[recordKey] || '';
      return Object.freeze({
        ...r,
        action,
        snapshot,
        label,
        time: new Date(r.created_at || r.updated_at).getTime(),
        recordKey,
        userKey: r.user_id ? String(r.user_id) : 'system',
        haystack: [
          r.revisionable_type, r.revisionable_id, label, r.key, r.old_value, r.new_value,
          r.user_name, r.user_role, r.user_second_role,
        ].filter(v => !isEmptyValue(v)).join('\n').toLowerCase(),
      });
    })
    .sort((a, b) => b.time - a.time || b.id - a.id);
}

export function isExpandable(item) {
  if (item.snapshot) return true;
  return [item.old_value, item.new_value].some(v => !isEmptyValue(v) && String(v).length > PREVIEW_LENGTH);
}

export function roleDisplay(item) {
  return [item.user_role, item.user_second_role].filter(Boolean).join(', ');
}

export function typeStyle(type) {
  let hash = 0;
  for (let i = 0; i < type.length; i++) hash = (hash * 31 + type.charCodeAt(i)) | 0;
  return { '--type-hue': TYPE_HUES[Math.abs(hash) % TYPE_HUES.length] };
}

const pad = n => String(n).padStart(2, '0');

export function formatTimestamp(time) {
  const d = new Date(time);
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function formatTime(time) {
  const d = new Date(time);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function dayLabel(time, now = Date.now()) {
  const d = new Date(time);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today - new Date(d).setHours(0, 0, 0, 0)) / DAY_MS);
  const date = d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  if (diffDays === 0) return `Today · ${date}`;
  if (diffDays === 1) return `Yesterday · ${date}`;
  return date;
}

export function relativeTime(time, now = Date.now()) {
  const seconds = Math.round((now - time) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(time).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
